import { useCallback, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  addDays,
  addMonths,
  buildCalendarGrid,
  compareDay,
  dayOfWeek,
  formatMonth,
  isSameDay,
  startOfMonth,
  todayPlain,
  weekdayLabels,
  type PlainDate,
} from './dateUtils.js';

export type CalendarSelection = 'single' | 'range';

export interface DateRange {
  start: PlainDate | null;
  end: PlainDate | null;
}

export interface CalendarProps {
  /** Locale for month/weekday labels. Defaults to the browser locale. */
  locale?: string;
  /** First day of the week. 0=Sun, 1=Mon, 6=Sat. Default 0. */
  weekStartsOn?: number;
  /** Selection mode — single day or range. Default `'single'`. */
  selection?: CalendarSelection;
  /** Controlled selected value. For single: a PlainDate or null.
   *  For range: a DateRange. */
  value?: PlainDate | DateRange | null;
  defaultValue?: PlainDate | DateRange | null;
  onChange?: (value: PlainDate | DateRange | null) => void;
  /** Min / max selectable dates. Days outside render as disabled. */
  min?: PlainDate;
  max?: PlainDate;
  /** Custom disabled check — runs in addition to min/max. */
  isDateDisabled?: (date: PlainDate) => boolean;
  /** Display the "today" tile with a ring. Default true. */
  showToday?: boolean;
  className?: string;
}

function isDisabled(
  d: PlainDate,
  min?: PlainDate,
  max?: PlainDate,
  custom?: (d: PlainDate) => boolean,
): boolean {
  /* Defensive: when min > max, treat as no bounds instead of silently
   * disabling every day. Logging would be nice but noisy at scale. */
  if (min !== undefined && max !== undefined && compareDay(min, max) > 0) {
    return custom?.(d) ?? false;
  }
  if (min !== undefined && compareDay(d, min) < 0) return true;
  if (max !== undefined && compareDay(d, max) > 0) return true;
  return custom?.(d) ?? false;
}

function isInRange(day: PlainDate, range: DateRange): boolean {
  if (range.start === null || range.end === null) return false;
  return compareDay(day, range.start) >= 0 && compareDay(day, range.end) <= 0;
}

/** Calendar grid with WAI-ARIA grid pattern keyboard support.
 *  https://www.w3.org/WAI/ARIA/apg/patterns/grid/
 *
 *  Keyboard:
 *    ← → ↑ ↓    move focus day by day / row by row
 *    PgUp/PgDn  previous / next month
 *    Shift+PgUp shift-year
 *    Home/End   start / end of the current week
 *    Enter/Space select the focused day
 *
 *  Selection:
 *    - `single` — click picks the day.
 *    - `range`  — click 1 picks start, click 2 picks end (auto-swaps
 *      if earlier), click 3 resets. Hovering after click 1 shows a
 *      preview of the range.
 */
export function Calendar(props: CalendarProps): ReactNode {
  const {
    locale,
    weekStartsOn: weekStartsOnRaw = 0,
    selection = 'single',
    value,
    defaultValue,
    onChange,
    min,
    max,
    isDateDisabled,
    showToday = true,
    className,
  } = props;

  const resolvedLocale =
    locale ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
  /* Clamp weekStartsOn into [0, 6] — values outside desync grid + day
   * header offsets. Floored to int in case caller passes a float. */
  const weekStartsOn = ((Math.floor(weekStartsOnRaw) % 7) + 7) % 7;

  /* Focused day — drives the focus ring. Month view follows focus. */
  const [focused, setFocused] = useState<PlainDate>(() => {
    const seed =
      value !== undefined && value !== null
        ? selection === 'range'
          ? ((value as DateRange).start ?? todayPlain())
          : (value as PlainDate)
        : defaultValue !== undefined && defaultValue !== null
          ? selection === 'range'
            ? ((defaultValue as DateRange).start ?? todayPlain())
            : (defaultValue as PlainDate)
          : todayPlain();
    return seed;
  });
  const [view, setView] = useState<PlainDate>(() => startOfMonth(focused));

  /* Internal selection mirrors controlled value when present. */
  const [internalSingle, setInternalSingle] = useState<PlainDate | null>(
    selection === 'single' ? ((defaultValue as PlainDate | null | undefined) ?? null) : null,
  );
  const [internalRange, setInternalRange] = useState<DateRange>(
    selection === 'range'
      ? ((defaultValue as DateRange | null | undefined) ?? { start: null, end: null })
      : { start: null, end: null },
  );
  const [rangePreview, setRangePreview] = useState<PlainDate | null>(null);

  const single =
    selection === 'single' ? ((value as PlainDate | null | undefined) ?? internalSingle) : null;
  const range =
    selection === 'range'
      ? ((value as DateRange | null | undefined) ?? internalRange)
      : { start: null, end: null };

  const grid = useMemo(() => buildCalendarGrid(view, weekStartsOn), [view, weekStartsOn]);
  const labels = useMemo(
    () => weekdayLabels(resolvedLocale, weekStartsOn),
    [resolvedLocale, weekStartsOn],
  );
  const today = todayPlain();

  const moveView = useCallback((months: number) => {
    setView((v) => addMonths(v, months));
  }, []);

  const selectDay = useCallback(
    (day: PlainDate) => {
      if (isDisabled(day, min, max, isDateDisabled)) return;
      if (selection === 'single') {
        if (value === undefined) setInternalSingle(day);
        onChange?.(day);
        setFocused(day);
      } else {
        const r = range;
        let next: DateRange;
        if (r.start === null || r.end !== null) {
          /* Fresh range: start a new selection. */
          next = { start: day, end: null };
        } else if (compareDay(day, r.start) < 0) {
          next = { start: day, end: r.start };
        } else {
          next = { start: r.start, end: day };
        }
        if (value === undefined) setInternalRange(next);
        onChange?.(next);
        setFocused(day);
      }
    },
    [selection, value, onChange, range, min, max, isDateDisabled],
  );

  const onDayKey = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, day: PlainDate) => {
      let target: PlainDate | null = null;
      if (e.key === 'ArrowLeft') target = addDays(day, -1);
      else if (e.key === 'ArrowRight') target = addDays(day, 1);
      else if (e.key === 'ArrowUp') target = addDays(day, -7);
      else if (e.key === 'ArrowDown') target = addDays(day, 7);
      else if (e.key === 'Home') {
        const dow = dayOfWeek(day);
        const offset = (dow - weekStartsOn + 7) % 7;
        target = addDays(day, -offset);
      } else if (e.key === 'End') {
        const dow = dayOfWeek(day);
        const offset = (dow - weekStartsOn + 7) % 7;
        target = addDays(day, 6 - offset);
      } else if (e.key === 'PageUp') {
        target = addMonths(day, e.shiftKey ? -12 : -1);
      } else if (e.key === 'PageDown') {
        target = addMonths(day, e.shiftKey ? 12 : 1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectDay(day);
        return;
      }
      if (target !== null) {
        e.preventDefault();
        setFocused(target);
        /* Keep view on the focused day's month. */
        if (target.month !== view.month || target.year !== view.year) {
          setView(startOfMonth(target));
        }
      }
    },
    [weekStartsOn, selectDay, view],
  );

  return (
    <div
      className={['vq-calendar', className].filter(Boolean).join(' ')}
      role="application"
      aria-label="Calendar"
      data-component="Calendar"
    >
      <header className="vq-calendar__header">
        <button
          type="button"
          className="vq-calendar__nav"
          aria-label="Previous month"
          onClick={() => {
            moveView(-1);
          }}
        >
          ‹
        </button>
        <span className="vq-calendar__month" aria-live="polite">
          {formatMonth(view, resolvedLocale)}
        </span>
        <button
          type="button"
          className="vq-calendar__nav"
          aria-label="Next month"
          onClick={() => {
            moveView(1);
          }}
        >
          ›
        </button>
      </header>
      <div className="vq-calendar__weekdays" role="row">
        {labels.map((l) => (
          <span key={l} className="vq-calendar__weekday" role="columnheader">
            {l}
          </span>
        ))}
      </div>
      <div
        className="vq-calendar__grid"
        role="grid"
        onMouseLeave={() => {
          setRangePreview(null);
        }}
      >
        {grid.weeks.map((row, wi) => (
          <div key={wi} role="row" className="vq-calendar__row">
            {row.map((day) => {
              const inMonth = day.month === view.month;
              const disabled = isDisabled(day, min, max, isDateDisabled);
              const isToday = showToday && isSameDay(day, today);
              const isFocused = isSameDay(day, focused);
              const selectedSingle =
                selection === 'single' && single !== null && isSameDay(day, single);
              const selectedRange =
                selection === 'range' &&
                ((range.start !== null && isSameDay(day, range.start)) ||
                  (range.end !== null && isSameDay(day, range.end)));
              const isStart =
                selection === 'range' && range.start !== null && isSameDay(day, range.start);
              const isEnd =
                selection === 'range' && range.end !== null && isSameDay(day, range.end);
              /* In-range state: confirmed range or preview while picking. */
              const inConfirmed =
                selection === 'range' &&
                range.start !== null &&
                range.end !== null &&
                isInRange(day, range);
              const inPreview =
                selection === 'range' &&
                range.start !== null &&
                range.end === null &&
                rangePreview !== null &&
                ((compareDay(day, range.start) >= 0 && compareDay(day, rangePreview) <= 0) ||
                  (compareDay(day, range.start) <= 0 && compareDay(day, rangePreview) >= 0));
              const inRange = inConfirmed || inPreview;
              const selected = selectedSingle || selectedRange;

              return (
                <button
                  key={`${String(day.year)}-${String(day.month)}-${String(day.day)}`}
                  type="button"
                  role="gridcell"
                  className="vq-calendar__day"
                  data-outside={!inMonth ? 'true' : 'false'}
                  data-today={isToday ? 'true' : 'false'}
                  data-selected={selected ? 'true' : 'false'}
                  data-in-range={inRange ? 'true' : 'false'}
                  data-range-start={isStart ? 'true' : 'false'}
                  data-range-end={isEnd ? 'true' : 'false'}
                  aria-selected={selected}
                  aria-disabled={disabled}
                  disabled={disabled}
                  tabIndex={isFocused ? 0 : -1}
                  onClick={() => {
                    selectDay(day);
                  }}
                  onKeyDown={(e) => {
                    onDayKey(e, day);
                  }}
                  onMouseEnter={() => {
                    if (selection === 'range' && range.start !== null && range.end === null) {
                      setRangePreview(day);
                    }
                  }}
                >
                  {day.day}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Re-export date helpers so DatePicker consumers can inspect values. */
export { daysInMonth, todayPlain, isSameDay, compareDay, formatDate } from './dateUtils.js';
export type { PlainDate } from './dateUtils.js';
